import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { cx } from '@/lib/cx';
import '../masters.css';

export interface ComboOption {
  value: string;
  label: string;
  /** 補足（2 行目に小さく表示） */
  description?: string;
  /** 検索対象の文字列（label・description に加えて照合する） */
  keywords?: string;
}

export interface ComboBoxProps {
  options: ReadonlyArray<ComboOption>;
  /** 選択中の value（空文字＝未選択） */
  value: string;
  /** 選択（または自由入力の確定）時。未選択に戻した場合は '' */
  onChange: (value: string, option?: ComboOption) => void;
  /** 一覧に無い文字列も入力値として許可する（港名など） */
  allowFreeText?: boolean;
  /** 選択中の value が options に無いときの表示 */
  fallbackLabel?: string;
  placeholder?: string;
  disabled?: boolean;
  invalid?: boolean;
  id?: string;
  width?: 'xs' | 'sm' | 'md' | 'lg' | 'full';
  loading?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  /** 一覧の幅を広げる（2 行表示の選択肢向け） */
  wideList?: boolean;
}

/** 検索用に正規化：NFKC・小文字・ひらがな→カタカナ・空白除去 */
export function normalizeSearch(s: string): string {
  return s
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[ぁ-ゖ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) + 0x60))
    .replace(/\s+/g, '');
}

/**
 * 検索できる選択部品（ARIA combobox）。キーボード：↑↓ 移動、Enter 確定、Esc 閉じる。
 * 一覧は入力文字で絞り込む（かな・全角半角・大文字小文字を区別しない）。
 */
export function ComboBox({
  options,
  value,
  onChange,
  allowFreeText,
  fallbackLabel,
  placeholder,
  disabled,
  invalid,
  id,
  width = 'md',
  loading,
  wideList,
  ...aria
}: ComboBoxProps) {
  const autoId = useId().replace(/:/g, '');
  const inputId = id ?? `cb${autoId}`;
  const listId = `${inputId}-list`;
  const selected = options.find((o) => o.value === value);
  const shownLabel = selected?.label ?? (value ? (allowFreeText ? value : (fallbackLabel ?? value)) : '');
  const [text, setText] = useState(shownLabel);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [dirty, setDirty] = useState(false);
  /** 矢印キーで候補を選んだか（自由入力時の Enter の挙動の切替に使う） */
  const [navigated, setNavigated] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // 外部から value が変わったら表示を同期（入力中は触らない）
  useEffect(() => {
    if (!dirty) setText(shownLabel);
  }, [shownLabel, dirty]);

  const filtered = useMemo(() => {
    const q = dirty ? normalizeSearch(text) : '';
    if (!q) return options;
    return options.filter((o) => normalizeSearch(`${o.label} ${o.description ?? ''} ${o.keywords ?? ''}`).includes(q));
  }, [options, text, dirty]);

  useEffect(() => {
    setActive(0);
  }, [text, open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) commitAndClose();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, text, dirty]);

  useEffect(() => {
    const el = listRef.current?.children[active] as HTMLElement | undefined;
    el?.scrollIntoView?.({ block: 'nearest' });
  }, [active]);

  function choose(o: ComboOption) {
    setDirty(false);
    setText(o.label);
    setOpen(false);
    onChange(o.value, o);
  }

  /** フォーカスアウト・外側クリック時：自由入力なら確定、そうでなければ元の表示へ戻す */
  function commitAndClose() {
    setOpen(false);
    if (dirty) {
      if (allowFreeText) {
        const t = text.trim();
        const hit = options.find((o) => o.label === t);
        if (hit) onChange(hit.value, hit);
        else onChange(t);
      } else {
        const t = normalizeSearch(text);
        const exact = t ? options.filter((o) => normalizeSearch(o.label) === t) : [];
        if (exact.length === 1) onChange(exact[0]!.value, exact[0]);
        else setText(shownLabel);
      }
      setDirty(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setNavigated(true);
      if (!open) setOpen(true);
      else setActive((a) => Math.min(a + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setNavigated(true);
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      if (!open) return;
      e.preventDefault();
      const exact = filtered.find((o) => normalizeSearch(o.label) === normalizeSearch(text));
      if (allowFreeText && dirty && !navigated) {
        if (exact) choose(exact);
        else commitAndClose();
      } else if (filtered[active]) choose(filtered[active]!);
      else commitAndClose();
    } else if (e.key === 'Escape') {
      if (open) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        setDirty(false);
        setText(shownLabel);
      }
    } else if (e.key === 'Tab') {
      commitAndClose();
    }
  }

  return (
    <span ref={wrapRef} className={cx('bs-combo', `bs-w-${width}`)}>
      <span className="bs-combo__control">
        <input
          id={inputId}
          type="text"
          role="combobox"
          autoComplete="off"
          className="bs-input bs-combo__input"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && filtered[active] ? `${listId}-${active}` : undefined}
          aria-invalid={invalid || undefined}
          aria-label={aria['aria-label']}
          aria-describedby={aria['aria-describedby']}
          placeholder={placeholder}
          disabled={disabled}
          value={text}
          onFocus={() => !disabled && setOpen(true)}
          onClick={() => !disabled && setOpen(true)}
          onChange={(e) => {
            setText(e.target.value);
            setNavigated(false);
            setDirty(true);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
        <span className="bs-combo__icons">
          {value && !disabled && (
            <button
              type="button"
              className="bs-combo__clear"
              aria-label="選択を解除"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setText('');
                setDirty(false);
                onChange('');
              }}
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown size={16} className="bs-combo__chevron" aria-hidden />
        </span>
      </span>
      {open && !disabled && (
        <ul ref={listRef} id={listId} role="listbox" className={cx('bs-combo__list', wideList && 'bs-combo__list--wide')}>
          {loading && <li className="bs-combo__empty">読み込み中…</li>}
          {!loading && filtered.length === 0 && (
            <li className="bs-combo__empty">{allowFreeText && text.trim() ? `「${text.trim()}」をそのまま入力します` : '該当する候補がありません'}</li>
          )}
          {filtered.map((o, i) => (
            <li
              key={o.value}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={o.value === value}
              className={cx('bs-combo__option', i === active && 'is-active')}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setActive(i)}
              onClick={() => choose(o)}
            >
              <span className="bs-combo__label">{o.label}</span>
              {o.description && <span className="bs-combo__desc">{o.description}</span>}
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}
